import React from 'react'
import { Box, Button, Stack } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'

const dataGridSx = {
  border: '1px solid',
  borderColor: 'grey.200',
  borderRadius: 2,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    fontWeight: 600,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: 'primary.contrastText',
  },
  '& .MuiDataGrid-iconButtonContainer': {
    color: 'primary.contrastText',
  },
  '& .MuiDataGrid-menuIcon': {
    color: 'primary.contrastText',
  },
  '& .MuiDataGrid-sortIcon': {
    color: 'primary.contrastText',
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'grey.50',
  },
}

interface DataGridTableProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any[]
  columns: GridColDef[]
  checkboxSelection?: boolean
  selectionModel?: GridRowSelectionModel
  onSelectionModelChange?: (model: GridRowSelectionModel) => void
  pageSize?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialState?: any
  onAdd?: () => void
  onDeleteSelected?: (selectedIds: GridRowSelectionModel) => void
  addLabel?: string
  deleteLabel?: string
}

export default function DataGridTable({
  rows,
  columns,
  checkboxSelection = false,
  selectionModel,
  onSelectionModelChange,
  pageSize = 25,
  initialState,
  onAdd,
  onDeleteSelected,
  addLabel = 'Add',
  deleteLabel = 'Remove selected',
}: DataGridTableProps) {
  const defaultInitialState = initialState || {
    pagination: { paginationModel: { pageSize } },
    sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
  }

  const hasSelection = selectionModel && selectionModel.length > 0

  return (
    <Box sx={{ width: '100%' }}>
      {(onAdd || onDeleteSelected) && (
        <Stack
          direction='row'
          spacing={1}
          sx={{ mb: 1, justifyContent: 'flex-end' }}
        >
          {onAdd && (
            <Button
              size='small'
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={onAdd}
            >
              {addLabel}
            </Button>
          )}
          {onDeleteSelected && (
            <Button
              size='small'
              variant='outlined'
              color='error'
              startIcon={<DeleteOutlineIcon />}
              onClick={() => onDeleteSelected(selectionModel ?? [])}
              disabled={!hasSelection}
            >
              {hasSelection && selectionModel
                ? `${deleteLabel} (${selectionModel.length})`
                : deleteLabel}
            </Button>
          )}
        </Stack>
      )}
      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id ?? row.code}
          checkboxSelection={checkboxSelection}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onSelectionModelChange}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={defaultInitialState}
          sx={dataGridSx}
          autoHeight
        />
      </Box>
    </Box>
  )
}

DataGridTable.defaultProps = {
  checkboxSelection: false,
  selectionModel: undefined,
  onSelectionModelChange: undefined,
  pageSize: 25,
  initialState: undefined,
  onAdd: undefined,
  onDeleteSelected: undefined,
  addLabel: 'Add',
  deleteLabel: 'Remove selected',
}
